/**
 * ScrollNavigator
 * Beobachtet den Scroll-Status des Dokumenteditors.
 *
 * Überwacht zwei Arten von Sprungmarken:
 * 1. AnnotationLink-Nodes (`[data-type="annotation-link"]`)
 *    → Fliegt zur verknüpften Annotation im 3D-Viewer
 * 2. ViewSnapshot-Nodes (`[data-type="view-snapshot"]`)
 *    → Stellt den vollständigen Viewer-Zustand wieder her (Kamera + ClipBox)
 *
 * „returnOnLeave" ist jetzt PRO SNAPSHOT konfigurierbar:
 * Jedes ViewSnapshot-Element kann `data-return-on-leave="true"` tragen.
 * Beim Verlassen einer so markierten Sprungmarke wird automatisch
 * zur vorherigen Kameraansicht zurückgekehrt.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { Editor } from '@tiptap/react'
import { ScrollNavEvent, AnnotationCamera, ClipBoxState } from '../../types/annotations'

/** Vollständiger Viewer-Zustand für die Rückkehr (Kamera + ClipBox) */
export interface CapturedViewerState {
  camera: AnnotationCamera
  clipBox: ClipBoxState | null
}

interface ScrollNavigatorOptions {
  editor: Editor | null
  onAnnotationVisible: (event: ScrollNavEvent) => void
  onSnapshotVisible?: (snapshotJson: string) => void
  /** Wird aufgerufen wenn ein Audit in den Viewport scrollt */
  onAuditVisible?: (auditId: string) => void
  /** Wird aufgerufen wenn eine Sprungmarke verlassen wird UND returnOnLeave aktiv ist */
  onReturnToPrevious?: (state: CapturedViewerState) => void
  /** Wird aufgerufen wenn eine Annotation die aktive Zone verlässt (z.B. Popup schließen) */
  onAnnotationLeave?: (annotationId: string) => void
  /** Vor dem Ansteuern einer Sprungmarke aktuellen Viewer-Zustand erfassen (Kamera + ClipBox) */
  captureCurrentState?: () => CapturedViewerState | null
  threshold?: number
  rootEl?: HTMLElement | null
  enabled?: boolean
}

interface ScrollNavigatorReturn {
  refresh: () => void
  activeAnnotationId: string | null
}

const MARKER_SELECTOR = '[data-type="annotation-link"], [data-type="view-snapshot"], [data-type="audit-link"]'

export function useScrollNavigator({
  editor,
  onAnnotationVisible,
  onSnapshotVisible,
  onAuditVisible,
  onReturnToPrevious,
  onAnnotationLeave,
  captureCurrentState,
  threshold = 0.5,
  rootEl = null,
  enabled = true,
}: ScrollNavigatorOptions): ScrollNavigatorReturn {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const mutationObserverRef = useRef<MutationObserver | null>(null)
  const onAnnotationVisibleRef = useRef(onAnnotationVisible)
  onAnnotationVisibleRef.current = onAnnotationVisible
  const onSnapshotVisibleRef = useRef(onSnapshotVisible)
  onSnapshotVisibleRef.current = onSnapshotVisible
  const onAuditVisibleRef = useRef(onAuditVisible)
  onAuditVisibleRef.current = onAuditVisible
  const onReturnRef = useRef(onReturnToPrevious)
  onReturnRef.current = onReturnToPrevious
  const onAnnotationLeaveRef = useRef(onAnnotationLeave)
  onAnnotationLeaveRef.current = onAnnotationLeave
  const captureStateRef = useRef(captureCurrentState)
  captureStateRef.current = captureCurrentState

  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null)

  // Debounce
  const lastFireRef = useRef(0)
  const DEBOUNCE_MS = 400

  // „Zurück zur letzten Ansicht" — gespeicherter Viewer-Zustand vor dem letzten Sprung
  const previousStateRef = useRef<CapturedViewerState | null>(null)
  // Trackt ob gerade eine Sprungmarke aktiv ist
  const activeMarkerRef = useRef<HTMLElement | null>(null)
  // Trackt ob das aktive Element returnOnLeave hat
  const activeReturnOnLeaveRef = useRef(false)

  const setupObserver = useCallback(() => {
    observerRef.current?.disconnect()
    mutationObserverRef.current?.disconnect()

    if (!editor || !enabled || !rootEl) return

    const dom = editor.view.dom as HTMLElement

    observerRef.current = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement

          // ── Element VERLÄSST die aktive Zone ─────────────────────
          if (!entry.isIntersecting) {
            if (el === activeMarkerRef.current) {
              // returnOnLeave: Zurück zur vorherigen Ansicht
              if (
                activeReturnOnLeaveRef.current &&
                previousStateRef.current
              ) {
                onReturnRef.current?.(previousStateRef.current)
                previousStateRef.current = null
                activeReturnOnLeaveRef.current = false
              }

              // Annotation verlässt Viewport → Popup schließen
              const dataType = el.getAttribute('data-type')
              if (dataType === 'annotation-link') {
                const annId = el.getAttribute('data-annotation-id')
                if (annId) {
                  setActiveAnnotationId(null)
                  onAnnotationLeaveRef.current?.(annId)
                }
              }

              activeMarkerRef.current = null

              // Visuelles Feedback entfernen
              el.classList.remove('is-active')
            }
            continue
          }

          // ── Element BETRITT die aktive Zone ──────────────────────
          const now = performance.now()
          if (now - lastFireRef.current < DEBOUNCE_MS) continue
          lastFireRef.current = now

          // Prüfe ob dieses Element returnOnLeave aktiviert hat
          const elementReturnOnLeave = el.getAttribute('data-return-on-leave') === 'true'

          // Aktuellen Viewer-Zustand sichern BEVOR der Sprung ausgeführt wird
          if (elementReturnOnLeave && captureStateRef.current) {
            const state = captureStateRef.current()
            if (state) {
              previousStateRef.current = state
            }
          }
          activeMarkerRef.current = el
          activeReturnOnLeaveRef.current = elementReturnOnLeave

          const dataType = el.getAttribute('data-type')

          // Visuelles Feedback
          rootEl.querySelectorAll(`${MARKER_SELECTOR}.is-active`)
            .forEach(e => e.classList.remove('is-active'))
          el.classList.add('is-active')

          // ── AnnotationLink ───────────────────────────────────────
          if (dataType === 'annotation-link') {
            const annotationId = el.getAttribute('data-annotation-id')
            if (!annotationId) continue

            const scrollProgress = rootEl.scrollHeight > 0
              ? rootEl.scrollTop / rootEl.scrollHeight : 0

            setActiveAnnotationId(annotationId)

            onAnnotationVisibleRef.current({
              annotationId,
              progress: Math.max(0, Math.min(1, scrollProgress)),
            })
          }

          // ── ViewSnapshot ─────────────────────────────────────────
          if (dataType === 'view-snapshot') {
            const snapshotJson = el.getAttribute('data-snapshot')
            if (!snapshotJson) continue
            onSnapshotVisibleRef.current?.(snapshotJson)
          }

          // ── AuditLink ──────────────────────────────────────────
          if (dataType === 'audit-link') {
            const auditId = el.getAttribute('data-audit-id')
            if (!auditId) continue
            onAuditVisibleRef.current?.(auditId)
          }
        }
      },
      {
        root: rootEl,
        rootMargin: '0px 0px -50% 0px',
        threshold: [0, threshold],  // 0 = leave-Erkennung, threshold = enter
      }
    )

    const observeAll = () => {
      if (!observerRef.current) return
      const markers = dom.querySelectorAll(MARKER_SELECTOR)
      markers.forEach(el => observerRef.current!.observe(el))
    }

    observeAll()

    mutationObserverRef.current = new MutationObserver(() => {
      observerRef.current?.disconnect()
      observeAll()
    })

    mutationObserverRef.current.observe(dom, {
      childList: true,
      subtree: true,
    })

  }, [editor, enabled, rootEl, threshold])

  useEffect(() => {
    setupObserver()
    return () => {
      observerRef.current?.disconnect()
      mutationObserverRef.current?.disconnect()
    }
  }, [setupObserver])

  // Cleanup
  useEffect(() => {
    if (!enabled && rootEl) {
      rootEl.querySelectorAll(`${MARKER_SELECTOR}.is-active`)
        .forEach(el => el.classList.remove('is-active'))
      setActiveAnnotationId(null)
      previousStateRef.current = null
      activeMarkerRef.current = null
      activeReturnOnLeaveRef.current = false
    }
  }, [enabled, rootEl])

  return {
    refresh: setupObserver,
    activeAnnotationId,
  }
}
